import { useMemo } from 'react';
import { useRefinementList } from 'react-instantsearch';

// Base interface for raw Algolia refinement items
export interface RawRefinementItem {
  label: string;
  value: string;
  count: number;
  isRefined: boolean;
}

// Generic base interface for refined items
export interface BaseRefinementItem {
  label: string;
  value: string;
  count: number;
  isRefined: boolean;
}

// Generic configuration interface
export interface RefinementConfig<TItem extends BaseRefinementItem, TMapping = any> {
  // Algolia attribute name
  attribute: string;

  // Mapping object for data transformation
  mapping: TMapping;

  // Function to validate if raw item is valid
  validateItem: (item: RawRefinementItem, mapping: TMapping) => boolean;

  // Function to transform raw item to refined item
  transformItem: (item: RawRefinementItem, mapping: TMapping) => TItem;

  // Function to sort refined items
  sortItems: (a: TItem, b: TItem) => number;

  // Optional grouping function for items that need consolidation
  groupItems?: (items: TItem[]) => TItem[];

  // Optional custom getter functions
  getters?: {
    [key: string]: (items: TItem[], ...args: any[]) => number;
  };
}

// Generic result interface
export interface GenericRefinementResult<TItem extends BaseRefinementItem> {
  items: TItem[];
  refine: (value: string) => void;
  refineMultiple: (values: string[]) => void;
  clearRefinements: () => void;
  isSearchStalled: boolean;
  canRefine: boolean;
  getTotalCount: () => number;
  // Dynamic getters based on configuration
  [key: string]: any;
}

/**
 * Generic refinement hook that eliminates code duplication across all refinement hooks
 * Uses configuration-based approach to handle different refinement types
 */
export function useGenericRefinement<TItem extends BaseRefinementItem, TMapping>(
  config: RefinementConfig<TItem, TMapping>,
): GenericRefinementResult<TItem> {
  const {
    items: rawItems,
    refine,
    canRefine,
  } = useRefinementList({
    attribute: config.attribute,
    sortBy: ['name:asc'],
  });

  // Transform and filter the raw items
  const items = useMemo(() => {
    // Filter valid items
    const validItems = rawItems.filter((item) =>
      config.validateItem(item, config.mapping),
    );

    // Transform items
    const transformedItems = validItems.map((item) =>
      config.transformItem(item, config.mapping),
    );

    // Sort items
    const sortedItems = transformedItems.sort(config.sortItems);

    // Apply grouping if needed (for items that need consolidation)
    return config.groupItems ? config.groupItems(sortedItems) : sortedItems;
  }, [rawItems, config]);

  // Common utility functions
  const refineMultiple = (values: string[]) => {
    values.forEach((value) => refine(value));
  };

  const clearRefinements = () => {
    const refinedItems = items.filter((item) => item.isRefined);
    refinedItems.forEach((item) => refine(item.value));
  };

  const getTotalCount = (): number =>
    items.reduce((total, item) => total + item.count, 0);

  // Build result object with dynamic getters
  const result: GenericRefinementResult<TItem> = {
    items,
    refine,
    refineMultiple,
    clearRefinements,
    isSearchStalled: false, // useRefinementList doesn't provide this
    canRefine,
    getTotalCount,
  };

  // Add custom getters if provided
  if (config.getters) {
    Object.entries(config.getters).forEach(([name, getter]) => {
      (result as any)[name] = (...args: any[]) => getter(items, ...args);
    });
  }

  return result as any;
}
